import React from 'react';
import { useNavigate } from 'react-router-dom';
import { C, DISP, BODY, MONO, FadeUp, Kicker, SectionH2, SECTION_PAD, useSectionView, track } from './shared';

/* All credit facts verified against /app/backend/helpers.py:
   TIER_MONTHLY_CREDITS: starter 20, growth 50, pro 100.
   CREDIT_COSTS: content/copy/email/seo/daily_plan 1, carousel 3, blog 3,
   landing_page 5, product_shoot 5, visual_ad 6, video_ad 8-12.
   Failed generations refunded (refund_credits). Monthly credits reset each
   calendar month; purchased top-up credits are stored separately and persist.
   Top-up packs start at $9.99 / 20 credits. */

const PLANS = [
  {
    id: 'starter', name: 'Starter', monthly: 0, sub: 'forever', tag: '',
    who: 'For trying ScalitOS with your real business. No card required.',
    example: '20 credits ≈ up to 20 written pieces, or 14 written pieces + 2 carousels.',
    features: ['20 credits per month', 'Daily Growth Plan', 'Basic content creation', '1 competitor scan profile', 'Marketing advisor (limited)', 'Carousels and flyers (watermarked)'],
    cta: 'Start for free', primary: false,
  },
  {
    id: 'growth', name: 'Growth', monthly: 19, sub: 'per month', tag: 'Most popular',
    who: 'For a solo founder marketing every week: content, emails and small campaigns.',
    example: '50 credits ≈ 30 written pieces + 4 carousels + 1 landing page + 3 emails.',
    features: ['50 credits per month', 'Everything in Starter', 'Full content engine', '5 competitor scan profiles', 'Campaign studio', 'Landing pages', 'Character creator', 'Email marketing', 'No watermarks'],
    cta: 'Get Growth', primary: true,
  },
  {
    id: 'pro', name: 'Pro', monthly: 29, sub: 'per month', tag: '',
    who: 'For founders launching campaigns with premium visuals and video.',
    example: '100 credits ≈ a 12s video ad + 2 visual ads + a product shoot + 4 carousels + a landing page + 40 written pieces.',
    features: ['100 credits per month', 'Everything in Growth', '3D Visual Ad Creator (6 cr)', 'Product Shoot Studio (5 cr)', 'Animation Series engine (3 cr/ep)', 'Video Ad Creator up to 12s (8 to 12 cr)', 'Meta Ads Manager', 'Priority creation queue', 'Weekly strategy report', 'Brand audit report'],
    cta: 'Get Pro', primary: false,
  },
];

const CREDIT_FACTS = [
  ['1 credit', 'One written asset: a post, caption, email, ad copy, SEO piece or daily plan.'],
  ['Bigger jobs cost more', 'Carousel 3 · Blog post 3 · Landing page 5 · Product shoot 5 · 3D visual ad 6 · Video ad 8 to 12. Every cost is shown in the app before you create.'],
  ['If a creation fails', 'Credits are automatically refunded to your balance.'],
  ['Run out?', 'Buy a top-up pack from $9.99, or upgrade. Monthly credits reset each month; top-up credits stay until you use them.'],
];

export const PricingSection = () => {
  const navigate = useNavigate();
  const ref = useSectionView('pricing');

  return (
    <section id="pricing" ref={ref} style={{ padding: SECTION_PAD }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeUp><Kicker>Pricing</Kicker></FadeUp>
        <FadeUp delay={80}>
          <SectionH2>
            Simple plans.<br />
            <span style={{ color: 'rgba(242,245,234,0.45)' }}>Credits you can actually understand.</span>
          </SectionH2>
        </FadeUp>

        <FadeUp delay={140}>
          <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(230px, 100%), 1fr))', gap: 12, padding: '22px 24px', border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface }}>
            {CREDIT_FACTS.map(([t, b], i) => (
              <div key={i}>
                <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 14, color: C.lime, marginBottom: 6 }}>{t}</p>
                <p style={{ fontFamily: BODY, fontSize: 13.5, lineHeight: 1.65, color: C.body }}>{b}</p>
              </div>
            ))}
          </div>
        </FadeUp>

        <div style={{ marginTop: 44, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(290px, 100%), 1fr))', gap: 18 }}>
          {PLANS.map((p, i) => {
            const price = p.monthly;
            return (
              <FadeUp key={p.id} delay={i * 100}>
                <div style={{
                  position: 'relative', padding: '34px 28px', borderRadius: 18, height: '100%', display: 'flex', flexDirection: 'column',
                  border: p.primary ? `1px solid ${C.borderLime}` : `1px solid ${C.border}`,
                  background: p.primary ? 'linear-gradient(180deg, rgba(200,241,53,0.05), rgba(0,0,0,0.2))' : C.surface,
                }}>
                  {p.tag && (
                    <span style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', padding: '5px 14px', borderRadius: 999, background: C.lime, color: C.bg, fontFamily: BODY, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{p.tag}</span>
                  )}
                  <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 21, color: C.text }}>{p.name}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 12 }}>
                    <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(36px, 5vw, 50px)', color: C.text, letterSpacing: '-0.03em', lineHeight: 1 }}>${price}</span>
                    <span style={{ fontFamily: BODY, fontSize: 13, color: C.muted }}>{p.sub}</span>
                  </div>
                  <p style={{ marginTop: 14, fontFamily: BODY, fontSize: 14, lineHeight: 1.65, color: C.body }}>{p.who}</p>
                  <p style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(200,241,53,0.05)', border: `1px solid ${C.borderLime}`, fontFamily: BODY, fontSize: 12.5, lineHeight: 1.6, color: C.text }}>{p.example}</p>
                  <div style={{ flex: 1, marginTop: 20, marginBottom: 24 }}>
                    {p.features.map((f, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                        <span aria-hidden="true" style={{ color: C.lime, fontFamily: MONO, fontSize: 12, marginTop: 3 }}>&#10003;</span>
                        <p style={{ fontFamily: BODY, fontSize: 13.5, color: C.body, lineHeight: 1.6 }}>{f}</p>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => { track('plan_selected', { plan: p.id }); navigate('/register'); }}
                    data-testid={`pricing-cta-${p.id}`}
                    className={p.primary ? 'scl-btn-primary' : 'scl-btn-ghost'}
                    style={{ width: '100%' }}
                  >
                    {price === 0 ? 'Start for free' : `${p.cta} →`}
                  </button>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};
